import api from "./api";
import type {
  TaskOrder,
  TaskOrderDetail,
  TaskOrderCreate,
  TaskOrderType,
  CooperationRequestCreate,
  TaskOrderDelegate,
  TaskOrderEscalate,
  TaskOrderReject,
  TaskOrderRecall,
  TaskOrderChangeDueDate,
  TaskOrderChangePriority,
  TaskOrderComplete,
  TaskOrderApproveCompletion,
  TaskOrderRequestRevision,
  TaskOrderCommentCreate,
  TaskOrderComment,
  KanbanRegistrationRequest,
  TaskOrderDashboardStats,
  TaskOrderFlowData,
  UserBasic,
  TaskOrderScopeGrant,
  ScopeGrantCreate,
  ScopeGrantUpdate,
  ScopeGrantOptions,
} from "@/types/taskOrder";
import type { User } from "@/types/user";

export interface TaskOrderSuggestDelegate {
  to_user_id: string;
  note?: string;
}

class TaskOrderService {
  private baseURL = "/task-orders";

  // ========================================================================
  // 생성 및 조회
  // ========================================================================

  async createTaskOrder(data: TaskOrderCreate): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(this.baseURL, data);
    return response.data;
  }

  async createCooperationRequest(data: CooperationRequestCreate): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/cooperation`, data);
    return response.data;
  }

  // 파생 지원 요청 (기존 업무에서 추가 인원에게 파생 하달)
  async deriveTaskOrder(id: string, data: TaskOrderCreate): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/derive`, data);
    return response.data;
  }

  async getMySuperior(): Promise<UserBasic> {
    const response = await api.get<UserBasic>(`${this.baseURL}/my-superior`);
    return response.data;
  }

  // 협조 요청 수신자 후보 — 회사 내 다른 부서 리더(본인·내 하위 부서 제외), 직속상위는 is_superior=true
  async getCooperationCandidates(): Promise<(User & { is_superior?: boolean })[]> {
    const response = await api.get<(User & { is_superior?: boolean })[]>(
      `${this.baseURL}/cooperation-candidates`,
    );
    return response.data;
  }

  async getOriginatedTaskOrders(status?: string, type?: TaskOrderType): Promise<TaskOrder[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (type) params.type = type;
    const response = await api.get<TaskOrder[]>(`${this.baseURL}/originated`, { params });
    return response.data;
  }

  async getReceivedTaskOrders(status?: string, type?: TaskOrderType): Promise<TaskOrder[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (type) params.type = type;
    const response = await api.get<TaskOrder[]>(`${this.baseURL}/received`, { params });
    return response.data;
  }

  // 내가 위임/전달한 뒤 하위에서 처리 중인 업무 (감독용)
  async getDelegatedTaskOrders(status?: string, type?: TaskOrderType): Promise<TaskOrder[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (type) params.type = type;
    const response = await api.get<TaskOrder[]>(`${this.baseURL}/delegated`, { params });
    return response.data;
  }

  // 내가 관여한 모든 업무 통합 (받은+하달한+위임한) — 모니터링용
  async getInvolvedTaskOrders(status?: string, type?: TaskOrderType): Promise<TaskOrder[]> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (type) params.type = type;
    const response = await api.get<TaskOrder[]>(`${this.baseURL}/involved`, { params });
    return response.data;
  }

  async getTaskOrderDetail(id: string): Promise<TaskOrderDetail> {
    const response = await api.get<TaskOrderDetail>(`${this.baseURL}/${id}`);
    return response.data;
  }

  // ========================================================================
  // 상태 변경 작업
  // ========================================================================

  async acknowledgeTaskOrder(id: string): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/acknowledge`);
    return response.data;
  }

  async delegateTaskOrder(id: string, data: TaskOrderDelegate): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/delegate`, data);
    return response.data;
  }

  async escalateTaskOrder(id: string, data: TaskOrderEscalate): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/escalate`, data);
    return response.data;
  }

  // 업무 이관 (원지시자가 지정한 대상에게 즉시 이관) — 대상 후보 조회
  // (현재 보유자의 조직도 한 단계 아래 + 보유자의 범위 위임 대상, via_scope_grant 플래그 포함)
  async getDelegateCandidates(id: string): Promise<User[]> {
    const response = await api.get<User[]>(`${this.baseURL}/${id}/delegate-candidates`);
    return response.data;
  }

  // ========================================================================
  // 범위 위임 (관리자) — 조직도 밖/옆으로 하달해야 하는 사람·부서 등록
  // ========================================================================

  private scopeURL = "/task-orders/scope-grants";

  async getScopeGrants(): Promise<TaskOrderScopeGrant[]> {
    const response = await api.get<TaskOrderScopeGrant[]>(this.scopeURL);
    return response.data;
  }

  async getScopeGrantOptions(): Promise<ScopeGrantOptions> {
    const response = await api.get<ScopeGrantOptions>(`${this.scopeURL}/options`);
    return response.data;
  }

  async createScopeGrant(data: ScopeGrantCreate): Promise<TaskOrderScopeGrant> {
    const response = await api.post<TaskOrderScopeGrant>(this.scopeURL, data);
    return response.data;
  }

  async updateScopeGrant(id: number, data: ScopeGrantUpdate): Promise<TaskOrderScopeGrant> {
    const response = await api.patch<TaskOrderScopeGrant>(`${this.scopeURL}/${id}`, data);
    return response.data;
  }

  async deleteScopeGrant(id: number): Promise<void> {
    await api.delete(`${this.scopeURL}/${id}`);
  }

  // 지금 나에게 적용 중인 범위 위임 (하달 작성 화면 안내용, 직원 전체 조회 가능)
  async getMyScopeGrants(): Promise<TaskOrderScopeGrant[]> {
    const response = await api.get<TaskOrderScopeGrant[]>(`${this.scopeURL}/mine`);
    return response.data;
  }

  async reassignTaskOrder(id: string, data: TaskOrderSuggestDelegate): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/reassign`, data);
    return response.data;
  }

  async rejectTaskOrder(id: string, data: TaskOrderReject): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/reject`, data);
    return response.data;
  }

  async recallTaskOrder(id: string, data: TaskOrderRecall): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/recall`, data);
    return response.data;
  }

  async nudgeTaskOrder(
    id: string
  ): Promise<{ success: boolean; message: string; cooldown_seconds?: number }> {
    const response = await api.post<{
      success: boolean;
      message: string;
      cooldown_seconds?: number;
    }>(`${this.baseURL}/${id}/nudge`);
    return response.data;
  }

  async cancelTaskOrder(id: string, data: { reason: string }): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/cancel`, data);
    return response.data;
  }

  async completeTaskOrder(id: string, data: TaskOrderComplete): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/complete`, data);
    return response.data;
  }

  async approveCompletion(id: string, data: TaskOrderApproveCompletion): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/approve-completion`, data);
    return response.data;
  }

  async requestRevision(id: string, data: TaskOrderRequestRevision): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/request-revision`, data);
    return response.data;
  }

  // ========================================================================
  // 우선순위 및 완료일 관리
  // ========================================================================

  async changeDueDate(id: string, data: TaskOrderChangeDueDate): Promise<TaskOrder> {
    const response = await api.patch<TaskOrder>(`${this.baseURL}/${id}/due-date`, data);
    return response.data;
  }

  async changePriority(id: string, data: TaskOrderChangePriority): Promise<TaskOrder> {
    const response = await api.patch<TaskOrder>(`${this.baseURL}/${id}/priority`, data);
    return response.data;
  }

  // ========================================================================
  // 코멘트
  // ========================================================================

  async addComment(id: string, data: TaskOrderCommentCreate): Promise<TaskOrderComment> {
    const response = await api.post<TaskOrderComment>(`${this.baseURL}/${id}/comments`, data);
    return response.data;
  }

  // ========================================================================
  // 칸반 통합
  // ========================================================================

  async registerToKanban(id: string, data: KanbanRegistrationRequest): Promise<TaskOrder> {
    const response = await api.post<TaskOrder>(`${this.baseURL}/${id}/register-kanban`, data);
    return response.data;
  }

  async syncToKanban(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `${this.baseURL}/${id}/sync-to-kanban`
    );
    return response.data;
  }

  // ========================================================================
  // 첨부파일
  // ========================================================================

  async uploadAttachment(id: string, file: File): Promise<{
    id: number;
    file_name: string;
    file_size: number;
    file_type: string;
    uploaded_by: string;
    created_at: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`${this.baseURL}/${id}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getAttachments(id: string): Promise<Array<{
    id: number;
    file_name: string;
    file_path: string;
    file_size: number;
    file_type: string;
    uploaded_by: string;
    uploader: { id: string; name: string } | null;
    created_at: string;
  }>> {
    const response = await api.get(`${this.baseURL}/${id}/attachments`);
    return response.data;
  }

  async deleteAttachment(taskOrderId: string, attachmentId: number): Promise<void> {
    await api.delete(`${this.baseURL}/${taskOrderId}/attachments/${attachmentId}`);
  }

  async downloadAttachment(taskOrderId: string, attachmentId: number): Promise<Blob> {
    const response = await api.get(
      `${this.baseURL}/${taskOrderId}/attachments/${attachmentId}/download`,
      { responseType: "blob" }
    );
    return response.data;
  }

  // ========================================================================
  // 대시보드
  // ========================================================================

  async getDashboardStats(): Promise<TaskOrderDashboardStats> {
    const response = await api.get<TaskOrderDashboardStats>(`${this.baseURL}/dashboard/stats`);
    return response.data;
  }

  async getDashboardFlows(
    statusFilter?: "IN_PROGRESS" | "COMPLETED",
    limit: number = 50,
    days?: number
  ): Promise<TaskOrderFlowData[]> {
    const params: Record<string, any> = { limit };
    if (statusFilter) {
      params.status_filter = statusFilter;
    }
    if (days != null) {
      params.days = days;
    }

    const response = await api.get<TaskOrderFlowData[]>(`${this.baseURL}/dashboard/flows`, {
      params,
    });
    return response.data;
  }
}

export const taskOrderService = new TaskOrderService();
